import { useEffect, useRef, type ReactNode } from 'react';
import { SquareMark } from '../components/common/MessagePage';
import { G } from './glyphs';
import { PendingButton } from '../components/common/PendingButton';
import { MessagePatricia } from './MessagePatricia';
import { claimFlight, fallbackSeat, invertTo, readPawSeat } from './pawFlight';

// THE WIZARD CHROME — the same on every step (AC3–AC7): the step bar, the
// pinned primary with its pending state, and the two failure lines.

export type StepError =
  /** A 429 — wait a minute (AC7). */
  | { kind: 'busy' }
  /** A named field refused the submit (AC14). */
  | { kind: 'field'; field: string }
  /**
   * THE STEP REFUSED ITSELF, before anything was sent.
   *
   * Registration is once-only, so a value the payload would silently drop has
   * to stop the submit — a cleared dog name, *Medication: Yes* with no
   * instructions, a half-typed date (2026-09-15). `field` marks the input;
   * this says what is wrong in words, because a marked input the customer has
   * scrolled past is not a message.
   */
  | { kind: 'incomplete'; message: string }
  /** Anything else — say what to do and offer Patricia (AC7). */
  | { kind: 'other' };

export function StepBar({ step, onBack, busy = false }: { step: 1 | 2; onBack?: () => void; busy?: boolean }) {
  const label = step === 1 ? 'Your details' : 'Your dogs';
  return (
    <div className="cp-stepbar">
      <div className="cp-row">
        {/* The prototype draws a back arrow on step 1 too; there is nothing to
            go back to, so it is not drawn (AC4, recorded at build). */}
        {/* AND NOT WHILE THE STEP IS SUBMITTING (PR #228 review, codex
            2026-09-21). The final submit stamps registration and lands on the
            receipt with whatever the details step holds; a Back tapped during
            the uploads let the customer type into fields that were never going
            to be sent again. */}
        {onBack && (
          <button type="button" className="cp-back" onClick={onBack} disabled={busy} aria-label="Back to your details">
            {G.back({ size: 18 })}
          </button>
        )}
        <div className="cp-steps">
          <i className="done"></i>
          <i className={step === 2 ? 'done' : ''}></i>
        </div>
        <span className="cp-sub">{step} of 2</span>
      </div>
      <div className="lbl">
        Registration · <b>{label}</b>
      </div>
    </div>
  );
}

/**
 * THE KENNEL'S MARK, AT THE TOP LEFT OF EVERY STEP — and the flight that puts
 * it there (Freddi, 2026-09-16).
 *
 * The wizard had no mark at all. A customer who has just been handed off to
 * Google and handed back lands on a form with a step bar and no sign of whose
 * form it is; the mark is the one thing on the door they will recognise, and it
 * belongs in the header for the same reason it is on the door.
 *
 * THE FLIGHT IS FLIP, INVERTED, WHICH IS WHY THE RESTING STATE IS THE REAL ONE.
 * The mark's home is its ordinary place in this header — no absolute
 * positioning, no cloned node, nothing to tidy up. The effect measures where it
 * has landed, applies the transform that would put it back where the door left
 * it, and then releases that transform. Everything that can fail — no note, a
 * rotated phone, storage refused, reduced motion, no script at all — fails to a
 * page that is already correct.
 *
 * TWO ELEMENTS, TWO CURVES, ON PURPOSE. One transform cannot carry a position
 * and a scale on different durations, so the outer span travels and the inner
 * one shrinks, and the shrink finishes 100ms before the travel does. That small
 * desync is the whole difference between an object arriving somewhere and a div
 * whose transform ended: real things reach their size before they stop moving.
 */
export function WizardBrand() {
  const outer = useRef<HTMLSpanElement>(null);
  const inner = useRef<HTMLSpanElement>(null);
  /**
   * THIS MOUNT HAS ALREADY WON THE FLIGHT — and may re-enter.
   *
   * StrictMode runs every effect twice in development: mount, clean up, mount
   * again. Without this ref the first run claimed the flight and parked the
   * mark in its inverted position, the cleanup cancelled the frame that would
   * have released it, and the second run found the flight already claimed and
   * returned early — leaving the paw stuck at `scale(2)` a third of the way
   * down the screen, permanently. Found by watching it, not by a test: nothing
   * that renders to static markup can see an effect that ran twice.
   *
   * The ref separates the two questions the guard was conflating. *Has this
   * sitting had its flight?* is `claimFlight`, and is answered once. *Is this
   * re-entry the same mount?* is this ref, and re-entry is allowed to proceed.
   */
  const mine = useRef(false);

  useEffect(() => {
    const move = outer.current;
    const scaleEl = inner.current;
    if (!move || !scaleEl) return;
    // ASKED FOR LESS MOTION MEANS NONE OF THIS. Not a shorter flight — a brand
    // mark crossing the screen is exactly the vestibular trigger the preference
    // exists for. The flight is still CLAIMED, so nothing else tries later.
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (!mine.current) {
      if (!claimFlight()) return;
      mine.current = true;
    }
    if (still) return;

    const rest = move.getBoundingClientRect();
    if (rest.width === 0) return;
    const { dx, dy, scale } = invertTo(readPawSeat() ?? fallbackSeat(), rest);
    // A flight of a few pixels is a twitch. Below this the mark is already
    // close enough to where the eye left it that moving is the wrong answer.
    if (Math.hypot(dx, dy) < 24) return;

    const page = move.closest('.cp-step');
    move.style.transition = 'none';
    scaleEl.style.transition = 'none';
    move.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
    scaleEl.style.transform = `scale(${scale})`;
    move.style.willChange = 'transform';
    scaleEl.style.willChange = 'transform';
    page?.classList.add('is-arriving');

    // TWO FRAMES, NOT ONE. A single rAF still lands inside the frame that set
    // the inverted transform often enough to matter, and the browser then
    // coalesces both values into no transition at all — the mark simply appears
    // in the header and the whole thing is silently dead.
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        move.style.transition = 'transform var(--cp-fly) var(--cp-fly-ease)';
        scaleEl.style.transition = 'transform var(--cp-fly-scale) var(--cp-fly-ease)';
        move.style.transform = '';
        scaleEl.style.transform = '';
      });
    });

    // THE RESET PUTS THE TRANSFORM BACK TOO, not just the transition.
    //
    // On `transitionend` the transform is already empty and clearing it is a
    // no-op. On an UNMOUNT MID-FLIGHT it is the whole point: leave the inverted
    // transform behind and the mark is parked off its seat for as long as the
    // node lives. That was the StrictMode bug above, and it is the same shape
    // as any real unmount — a customer who taps Back while the paw is moving.
    const done = () => {
      move.style.willChange = '';
      scaleEl.style.willChange = '';
      move.style.transition = '';
      scaleEl.style.transition = '';
      move.style.transform = '';
      scaleEl.style.transform = '';
    };
    move.addEventListener('transitionend', done, { once: true });
    return () => {
      cancelAnimationFrame(raf);
      move.removeEventListener('transitionend', done);
      page?.classList.remove('is-arriving');
      done();
    };
  }, []);

  return (
    <div className="cp-brand">
      <span className="cp-paw" ref={outer}>
        <span className="cp-paw-in" ref={inner}>
          <SquareMark />
        </span>
      </span>
      <span className="cp-brand-name">Beeches Brook</span>
    </div>
  );
}

/** One page of the wizard: fits the phone, the actions pinned at the foot (AC5). */
export function StepPage({ children, onSubmit }: { children: ReactNode; onSubmit: () => void }) {
  return (
    <main className="cp-app cp-wizard">
      <form
        className="cp-scroll cp-step"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        noValidate
      >
        <WizardBrand />
        {children}
      </form>
    </main>
  );
}

/** The one primary, full width, 46px, with an icon — pending in its own dimensions (AC6). */
export function PrimarySubmit({ busy, icon, children }: { busy: boolean; icon: ReactNode; children: ReactNode }) {
  return (
    <PendingButton type="submit" className="mp-btn primary" icon={icon} busy={busy}>
      {children}
    </PendingButton>
  );
}

/** What a failed submit says — never a raw error, never a blank (AC7). */
export function StepFailure({ error }: { error: StepError | null }) {
  if (!error || error.kind === 'field') return null;
  if (error.kind === 'incomplete') {
    return <p className="cp-fail" role="alert">{error.message}</p>;
  }
  if (error.kind === 'busy') {
    return (
      <p className="cp-fail" role="alert">
        Give it a minute — a few too many tries just now. Wait a minute and try again.
      </p>
    );
  }
  return (
    <div className="cp-fail" role="alert">
      <p>That didn’t go through. Check your connection and try again — or message Patricia and she’ll sort it.</p>
      <MessagePatricia text="my registration isn’t going through — " />
    </div>
  );
}
